import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaRoute } from "react-icons/fa";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  FaLocationArrow,
  FaUser,
  FaMapMarkedAlt,
  FaCircle,
  FaRoad,
  FaClock,
  FaRuler,
} from "react-icons/fa";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const STATUS_COLORS = {
  active: "#10B981", // green
  inactive: "#EF4444", // red
  idle: "#F59E0B", // amber
};

const STATUS_THRESHOLDS = {
  active: 2, // minutes
  idle: 5,
  inactive: Infinity,
};

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isFollowingUser, setIsFollowingUser] = useState(false);
  const markers = useRef({});
  const popups = useRef({});
  const routeLayers = useRef({});
  const shouldFitBounds = useRef(true);
  const lastZoomLevel = useRef(12); // Default zoom level

  // Admin's fixed location
  const adminLocation = {
    latitude: 31.57990793453379,
    longitude: 74.35504625463933,
  };

  // Determine user status based on last update time
  const getUserStatus = useCallback((updatedAt) => {
    const lastUpdate = new Date(updatedAt);
    const now = new Date();
    const minutesDiff = (now - lastUpdate) / (1000 * 60);

    if (minutesDiff < STATUS_THRESHOLDS.active) return "active";
    if (minutesDiff < STATUS_THRESHOLDS.idle) return "idle";
    return "inactive";
  }, []);

  // Fetch user locations
  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/locations`
      );
      const usersWithStatus = res.data.map((user) => ({
        ...user,
        status: getUserStatus(user.updatedAt),
      }));
      setUsers(usersWithStatus);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  }, [getUserStatus]);

  // Remove all route layers except the specified one
  const removeOtherRouteLayers = useCallback((keepUserId) => {
    Object.keys(routeLayers.current).forEach(userId => {
      if (userId !== keepUserId && routeLayers.current[userId]) {
        if (map.current.getLayer(routeLayers.current[userId])) {
          map.current.removeLayer(routeLayers.current[userId]);
        }
        if (map.current.getLayer(`${routeLayers.current[userId]}-glow`)) {
          map.current.removeLayer(`${routeLayers.current[userId]}-glow`);
        }
        if (map.current.getLayer(`${routeLayers.current[userId]}-arrows`)) {
          map.current.removeLayer(`${routeLayers.current[userId]}-arrows`);
        }
        if (map.current.getSource(routeLayers.current[userId])) {
          map.current.removeSource(routeLayers.current[userId]);
        }
        delete routeLayers.current[userId];
      }
    });
  }, []);

  // Show route to user using Mapbox Directions API
  const showRouteToUser = useCallback(async (user) => {
    if (!map.current || !user) return;

    try {
      // Remove all other routes first
      removeOtherRouteLayers(user.userId);

      const start = [adminLocation.longitude, adminLocation.latitude];
      const end = [user.longitude, user.latitude];

      // Get directions with more detailed routing
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?` +
          `geometries=geojson&steps=true&overview=full&annotations=duration,distance&access_token=${mapboxgl.accessToken}`
      );

      const data = await response.json();
      const route = data.routes[0].geometry;

      // Generate a unique ID for this route layer
      const routeId = `route-${user.userId}-${Date.now()}`;
      routeLayers.current[user.userId] = routeId;

      // Add the route to the map with more prominent styling
      map.current.addSource(routeId, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: route,
        },
      });

      // Main route line
      map.current.addLayer({
        id: routeId,
        type: "line",
        source: routeId,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#3b82f6",
          "line-width": 5,
          "line-opacity": 0.9,
          "line-dasharray": [0, 0],
        },
      });

      // Add a subtle glow effect
      map.current.addLayer({
        id: `${routeId}-glow`,
        type: "line",
        source: routeId,
        paint: {
          "line-color": "#3b82f6",
          "line-width": 10,
          "line-opacity": 0.2,
          "line-blur": 5,
        },
      });

      // Add direction arrows along the route
      map.current.addLayer({
        id: `${routeId}-arrows`,
        type: "symbol",
        source: routeId,
        layout: {
          "symbol-placement": "line",
          "text-field": "▶",
          "text-size": ["interpolate", ["linear"], ["zoom"], 12, 16, 16, 24],
          "symbol-spacing": [
            "interpolate",
            ["linear"],
            ["zoom"],
            12,
            30,
            16,
            60,
          ],
          "text-keep-upright": false,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#3b82f6",
          "text-halo-width": 2,
        },
      });

      // Calculate distance and duration
      const distance = (data.routes[0].distance / 1000).toFixed(1);
      const duration = Math.floor(data.routes[0].duration / 60);

      // Update popup with route info
      if (popups.current[user.userId]) {
        popups.current[user.userId].setHTML(`
          <div class="popup-content">
            <h3><FaUser /> User ${user.userId}</h3>
            <p>Status: <span style="color: ${STATUS_COLORS[user.status]}">${
              user.status
            }</span></p>
            <p>Lat: ${user.latitude.toFixed(6)}</p>
            <p>Lng: ${user.longitude.toFixed(6)}</p>
            <p>Last update: ${new Date(user.updatedAt).toLocaleTimeString()}</p>
            <div class="route-info">
              <p><FaRuler /> Distance: ${distance} km</p>
              <p><FaClock /> Duration: ~${duration} min</p>
            </div>
            <div class="popup-buttons">
              <button class="focus-btn" data-userid="${
                user.userId
              }">Follow</button>
              <button class="route-btn" data-userid="${
                user.userId
              }">Show Route</button>
            </div>
          </div>
        `);

        // Reattach event listeners after updating HTML
        const popupElement = popups.current[user.userId].getElement();
        const focusBtn = popupElement.querySelector('.focus-btn');
        const routeBtn = popupElement.querySelector('.route-btn');
        
        if (focusBtn) {
          focusBtn.onclick = () => focusOnUser(user);
        }
        if (routeBtn) {
          routeBtn.onclick = () => showRouteToUser(user);
        }
      }

      // Fly to the route with padding but maintain zoom level if already zoomed in
      const coordinates = route.coordinates;
      const routeBounds = coordinates.reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds()
      );

      const currentZoom = map.current.getZoom();
      const targetZoom = currentZoom > 12 ? currentZoom : 12;

      map.current.fitBounds(routeBounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        zoom: targetZoom,
        duration: 1000,
      });

      setIsFollowingUser(true);
    } catch (error) {
      console.error("Error fetching route:", error);
    }
  }, [removeOtherRouteLayers]);

  // Create custom marker element
  const createCustomMarkerElement = useCallback((type, status = "active") => {
    const el = document.createElement("div");
    el.className = `custom-marker ${type}`;
    
    if (type === "admin") {
      el.innerHTML = `
        <div class="map-marker admin">
          <img src="https://img.icons8.com/?size=100&id=7880&format=png&color=000000" 
               alt="Admin" 
               style="width: 32px; height: 32px;"/>
        </div>
      `;
    } else {
      const markerImage = status === "inactive" 
        ? "https://img.icons8.com/?size=100&id=BE8zKVHfRaQj&format=png&color=000000"
        : status === "idle"
        ? "/Idle.png"
        : "/ActiveDriver.gif";
      
      el.innerHTML = `
        <div class="map-marker ${status}">
          <img src="${markerImage}" 
               alt="User" 
               style="width: 28px; height: 28px;"/>
          ${status === "active" ? '<div class="pulse-effect"></div>' : ''}
        </div>
      `;
    }
    
    return el;
  }, []);

  // Focus on selected user
  const focusOnUser = useCallback(
    (user) => {
      if (!map.current || !user) return;

      map.current.flyTo({
        center: [user.longitude, user.latitude],
        zoom: 14,
        essential: true,
      });

      setSelectedUser(user);
      setIsFollowingUser(true);
      showRouteToUser(user);
    },
    [showRouteToUser]
  );

  // Initialize map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [adminLocation.longitude, adminLocation.latitude],
      zoom: lastZoomLevel.current,
    });

    // Add controls
    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");

    // Add geolocate control
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserLocation: true,
      showAccuracyCircle: false,
    });
    map.current.addControl(geolocate, "top-right");

    // Store zoom level when changed by user
    map.current.on("zoomend", () => {
      lastZoomLevel.current = map.current.getZoom();
    });

    // Handle map move events
    map.current.on("moveend", () => {
      if (map.current) {
        setIsFollowingUser(false);
      }
    });

    map.current.on("load", () => {
      setMapLoaded(true);

      // Add admin marker with larger size
      new mapboxgl.Marker({
        element: createCustomMarkerElement("admin"),
        anchor: "center",
      })
        .setLngLat([adminLocation.longitude, adminLocation.latitude])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div class="popup-content">
          <h3><FaMapMarkedAlt /> Admin Location</h3>
          <strong>Admin is currently at: <span style="color: #10B981">UET,Main Campus</span></strong>
          <p>Lat: ${adminLocation.latitude.toFixed(6)}</p>
          <p>Lng: ${adminLocation.longitude.toFixed(6)}</p>
        </div>
      `)
        )
        .addTo(map.current);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [createCustomMarkerElement]);

  // Update user markers and data
  useEffect(() => {
    if (!map.current || !mapLoaded || users.length === 0) return;

    try {
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([adminLocation.longitude, adminLocation.latitude]);

      // Update markers for each user
      users.forEach((user) => {
        const userId = user.userId;
        const currentLocation = [user.longitude, user.latitude];
        bounds.extend(currentLocation);

        // Remove existing popup if it exists
        if (popups.current[userId]) {
          popups.current[userId].remove();
          delete popups.current[userId];
        }

        // Update or create marker
        if (!markers.current[userId]) {
          markers.current[userId] = new mapboxgl.Marker({
            element: createCustomMarkerElement("user", user.status),
            anchor: "center",
          })
            .setLngLat(currentLocation)
            .addTo(map.current);
        } else {
          // Update existing marker with new status
          const markerElement = markers.current[userId].getElement();
          const markerImage = user.status === "inactive" 
            ? "https://img.icons8.com/?size=100&id=BE8zKVHfRaQj&format=png&color=000000"
            : user.status === "idle"
            ? "/Idle.png"
            : "/ActiveDriver.gif";
          
          markerElement.innerHTML = `
            <div class="map-marker ${user.status}">
              <img src="${markerImage}" 
                   alt="User" 
                   style="width: 28px; height: 28px;"/>
              ${user.status === "active" ? '<div class="pulse-effect"></div>' : ''}
            </div>
          `;
          markers.current[userId].setLngLat(currentLocation).setPopup(null);
        }

        // Create new popup with updated info
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(`
            <div class="popup-content">
              <h3><FaUser /> User ${userId}</h3>
              <p>Status: <span style="color: ${STATUS_COLORS[user.status]}">${
                user.status
              }</span></p>
              <p>Lat: ${user.latitude.toFixed(6)}</p>
              <p>Lng: ${user.longitude.toFixed(6)}</p>
              <p>Last update: ${new Date(
                user.updatedAt
              ).toLocaleTimeString()}</p>
              <div class="popup-buttons">
                <button class="focus-btn" data-userid="${userId}">Follow</button>
                <button class="route-btn" data-userid="${userId}">Show Route</button>
              </div>
            </div>
          `);

        // Store popup reference
        popups.current[userId] = popup;
        
        // Set popup to marker
        markers.current[userId].setPopup(popup);

        // Add click handler to show route when popup opens
        popup.on("open", () => {
          setSelectedUser(user);
          showRouteToUser(user);
        });

        // Add event listeners to buttons after popup is created
        popup.on('open', () => {
          const popupElement = popup.getElement();
          const focusBtn = popupElement.querySelector('.focus-btn');
          const routeBtn = popupElement.querySelector('.route-btn');
          
          if (focusBtn) {
            focusBtn.onclick = () => focusOnUser(user);
          }
          if (routeBtn) {
            routeBtn.onclick = () => showRouteToUser(user);
          }
        });
      });

      // Only fit bounds on initial load or if not following a specific user
      if (shouldFitBounds.current && !isFollowingUser) {
        // Check if bounds are valid
        if (
          bounds.getNorth() !== bounds.getSouth() &&
          bounds.getEast() !== bounds.getWest()
        ) {
          map.current.fitBounds(bounds, {
            padding: 100,
            maxZoom: 14,
            duration: 1000,
          });
        }
        shouldFitBounds.current = false;
      }
    } catch (error) {
      console.error("Error updating map:", error);
    }
  }, [users, mapLoaded, isFollowingUser, createCustomMarkerElement, showRouteToUser, focusOnUser]);

  // Fetch data periodically
  useEffect(() => {
    fetchLocations();
    const interval = setInterval(fetchLocations, 5000);
    return () => clearInterval(interval);
  }, [fetchLocations]);

  return (
    <div className="admin-dashboard">
      <div className="map-container" ref={mapContainer} />

      <div className="user-list-container">
        <div className="panel-header">
          <Link to="/routes" className="view-routes-button">
            <FaRoute /> View All Routes
          </Link>
          <h2>
            <FaUser /> Tracked Users ({users.length})
          </h2>
          <div className="status-legend">
            <div>
              <span
                className="legend-color"
                style={{ backgroundColor: STATUS_COLORS.active }}
              ></span>{" "}
              Active
            </div>
            <div>
              <span
                className="legend-color"
                style={{ backgroundColor: STATUS_COLORS.idle }}
              ></span>{" "}
              Idle
            </div>
            <div>
              <span
                className="legend-color"
                style={{ backgroundColor: STATUS_COLORS.inactive }}
              ></span>{" "}
              Inactive
            </div>
          </div>
        </div>
        <div className="user-list">
          {users.map((user) => (
            <div
              key={user.userId}
              className={`user-card ${
                selectedUser?.userId === user.userId ? "active" : ""
              }`}
              onClick={() => focusOnUser(user)}
            >
              <div
                className="user-icon"
                style={{ color: STATUS_COLORS[user.status] }}
              >
                <FaUser />
              </div>
              <div className="user-info">
                <h3>User {user.userId}</h3>
                <p
                  className="user-status"
                  style={{ color: STATUS_COLORS[user.status] }}
                >
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </p>
                <p>
                  Last update: {new Date(user.updatedAt).toLocaleTimeString()}
                </p>
                <p>
                  Distance:{" "}
                  {calculateDistance(
                    adminLocation.latitude,
                    adminLocation.longitude,
                    user.latitude,
                    user.longitude
                  ).toFixed(2)}{" "}
                  km
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate distance between two points in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default AdminDashboard;